"use client"

import { createContext, useContext, useEffect, useState, type ReactNode } from "react"
import { useAuth } from "@/contexts/auth-context"
import { useLanguage } from "@/contexts/language-context"
import { codeService } from "@/lib/code-service"
import type { GroupedCodeValues, CodeItem } from "@/types/code-values"

// Declare global window property
declare global {
  interface Window {
    PhoenixCodeValues?: GroupedCodeValues
  }
}

type CodeContextType = {
  codeValues: GroupedCodeValues
  isLoading: boolean
  error: string | null
  getGroupItems: (groupName: string) => CodeItem[]
  getCodeItem: (groupName: string, code: string) => CodeItem | null
  getCodeText: (groupName: string, code: string) => string
  getCodeDescription: (groupName: string, code: string) => string
  getAllGroups: () => string[]
  refreshCodeValues: () => Promise<void>
  isMockData: boolean
}

const CodeContext = createContext<CodeContextType | undefined>(undefined)

export function CodeProvider({ children }: { children: ReactNode }) {
  const { token, isAuthenticated } = useAuth()
  const { dictionary } = useLanguage()
  const [codeValues, setCodeValues] = useState<GroupedCodeValues>({})
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)
  const [isMockData, setIsMockData] = useState<boolean>(false) // Force to false to ensure real API requests

  // Function to load code values
  const loadCodeValues = async () => {
    console.log("Loading code values... forcing real API request")

    // Clear any existing code values from the window object to ensure a fresh load
    if (typeof window !== "undefined" && window.PhoenixCodeValues) {
      console.log("Clearing existing code values from window object")
      window.PhoenixCodeValues = undefined
    }

    if (!isAuthenticated) {
      setIsLoading(false)
      setError(dictionary?.codeValues?.errors?.notAuthenticated || "Authentication required to load code values")
      return
    }

    try {
      setIsLoading(true)
      setError(null)

      console.log("Fetching code values from API...")

      // Fetch the raw response from the API
      const response = await codeService.getRawCodeValues(token)

      console.log("Code values response received:", response)

      // Process the response using the provided reducer function
      if (response.Items && Array.isArray(response.Items)) {
        console.log(`Processing ${response.Items.length} code value items...`)

        const processedValues = response.Items.reduce(
          (result, item) => ({
            ...result,
            [item.groupName]: [...(result[item.groupName] || []), item],
          }),
          {} as GroupedCodeValues,
        )

        console.log(`Processed into ${Object.keys(processedValues).length} groups`)

        // Store in state
        setCodeValues(processedValues)
        setIsMockData(false)

        // Store in window object for global access
        if (typeof window !== "undefined") {
          window.PhoenixCodeValues = processedValues
          console.log("Stored code values in window.PhoenixCodeValues")
        }
      } else {
        console.error("Invalid response format:", response)
        setError("Invalid response format from code values API")
      }
    } catch (err) {
      console.error("Failed to load code values:", err)
      setError(dictionary?.codeValues?.errors?.loadFailed || "Failed to load code values. Please try again later.")
    } finally {
      setIsLoading(false)
    }
  }

  // Function to manually refresh code values
  const refreshCodeValues = async () => {
    await loadCodeValues()
  }

  // Load code values when the application loads and authentication is available
  useEffect(() => {
    if (isAuthenticated && token) {
      loadCodeValues()
    }
  }, [isAuthenticated, token, dictionary])

  // Helper function to get all items in a group
  const getGroupItems = (groupName: string): CodeItem[] => {
    return codeValues[groupName] || []
  }

  // Helper function to get a specific code item by group name and code
  const getCodeItem = (groupName: string, code: string): CodeItem | null => {
    const group = getGroupItems(groupName)
    return group.find((item) => item.code === code) || null
  }

  // Helper function to get the text for a code
  const getCodeText = (groupName: string, code: string): string => {
    const item = getCodeItem(groupName, code)
    return item?.text || code
  }

  // Helper function to get the description for a code
  const getCodeDescription = (groupName: string, code: string): string => {
    const item = getCodeItem(groupName, code)
    return item?.description || code
  }

  // Helper function to get all group names
  const getAllGroups = (): string[] => {
    return Object.keys(codeValues)
  }

  return (
    <CodeContext.Provider
      value={{
        codeValues,
        isLoading,
        error,
        getGroupItems,
        getCodeItem,
        getCodeText,
        getCodeDescription,
        getAllGroups,
        refreshCodeValues,
        isMockData,
      }}
    >
      {children}
    </CodeContext.Provider>
  )
}

export function useCode() {
  const context = useContext(CodeContext)
  if (context === undefined) {
    throw new Error("useCode must be used within a CodeProvider")
  }
  return context
}
